import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle, Loader2, Info, TrendingUp, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Progress } from '@/Components/ui/progress';
import { Alert, AlertDescription } from '@/Components/ui/alert';
import { Badge } from '@/Components/ui/badge';
import { ErrorGuidanceCard } from './ErrorHelper';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

/**
 * Modal de progresso de importação com feedback em tempo real
 * Mostra item por item sendo processado via WebSocket
 */
const ImportProgressModal = ({ isOpen, onClose, importData, socketEvents }) => {
  useBodyScrollLock(isOpen);
  const [progress, setProgress] = useState({
    total: 0,
    processed: 0,
    created: 0,
    updated: 0,
    errors: 0,
    currentRow: 0
  });
  
  const [items, setItems] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const logEndRef = useRef(null);

  // Atualizar progresso com eventos do socket
  useEffect(() => {
    if (!socketEvents) return;

    if (socketEvents.event === 'start') {
      setProgress(prev => ({ ...prev, total: socketEvents.data.total }));
      setItems([]);
      setIsComplete(false);
    }

    if (socketEvents.event === 'item_created') {
      setProgress(prev => ({
        ...prev,
        processed: socketEvents.data.processed,
        created: prev.created + 1,
        currentRow: socketEvents.data.row
      }));
      
      setItems(prev => [...prev, {
        row: socketEvents.data.row,
        status: 'success',
        action: 'created',
        id: socketEvents.data.id,
        message: `Nova parceria criada (ID: ${socketEvents.data.id})`
      }]);
    }

    if (socketEvents.event === 'item_updated') {
      setProgress(prev => ({
        ...prev,
        processed: socketEvents.data.processed,
        updated: prev.updated + 1,
        currentRow: socketEvents.data.row
      }));
      
      setItems(prev => [...prev, {
        row: socketEvents.data.row,
        status: 'success',
        action: 'updated',
        id: socketEvents.data.id,
        message: `Parceria atualizada (ID: ${socketEvents.data.id})`
      }]);
    }

    if (socketEvents.event === 'item_error') {
      setProgress(prev => ({
        ...prev,
        processed: socketEvents.data.processed,
        errors: prev.errors + 1,
        currentRow: socketEvents.data.row
      }));
      
      setItems(prev => [...prev, {
        row: socketEvents.data.row,
        status: 'error',
        action: 'error',
        errors: socketEvents.data.errors,
        message: socketEvents.data.errors.map(e => `${e.field}: ${e.message}`).join(', ')
      }]);
    }

    if (socketEvents.event === 'complete') {
      setIsComplete(true);
      setProgress({
        total: socketEvents.data.total,
        processed: socketEvents.data.processed,
        created: socketEvents.data.created,
        updated: socketEvents.data.updated,
        errors: socketEvents.data.errors,
        currentRow: socketEvents.data.total
      });
    }

    if (socketEvents.event === 'error') {
      setIsComplete(true);
      // Marcar como erro crítico
      setProgress(prev => ({
        ...prev,
        errors: prev.errors + 1
      }));
    }

  }, [socketEvents]);

  // Atualizar com dados finais da API
  useEffect(() => {
    if (importData && importData.results) {
      setProgress({
        total: importData.results.total,
        processed: importData.results.processed,
        created: importData.results.created,
        updated: importData.results.updated,
        errors: importData.results.errors,
        currentRow: importData.results.total
      });
      setIsComplete(true);
      
      if (importData.recommendations) {
        setRecommendations(importData.recommendations);
      }
    }
  }, [importData]);

  const progressPercentage = progress.total > 0 
    ? Math.round((progress.processed / progress.total) * 100) 
    : 0;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Loader2 className="w-4 h-4 animate-spin text-blue-600" />;
    }
  };

  const getActionBadge = (action) => {
    switch (action) {
      case 'created':
        return <Badge className="bg-green-100 text-green-800">Nova</Badge>;
      case 'updated':
        return <Badge className="bg-blue-100 text-blue-800">Atualizada</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-800">Erro</Badge>;
      default:
        return null;
    }
  };

  const getRecommendationIcon = (type) => {
    switch (type) {
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
      case 'tip':
        return <TrendingUp className="w-4 h-4 text-blue-600" />;
      default:
        return <Info className="w-4 h-4 text-blue-600" />;
    }
  };

  // Auto-scroll para o final do log quando novos itens são adicionados
  useEffect(() => {
    if (!isComplete && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [items, isComplete]);

  // Separar itens com erro dos demais
  const errorItems = items.filter(item => item.status === 'error');
  const successItems = items.filter(item => item.status === 'success');

  if (!isOpen) return null;

  // Portal: escapa do fluxo normal da página, evitando que containers com
  // margin/spacing (ex.: space-y-*) desloquem o overlay fixed.
  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50"
        onClick={(e) => e.target === e.currentTarget && isComplete && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          <Card className="flex flex-col h-full max-h-[90vh]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 sm:pb-4 border-b shrink-0">
              <CardTitle className="text-lg sm:text-xl font-bold flex items-center gap-2">
                {isComplete ? (
                  <>
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                    <span className="hidden sm:inline">Importação Concluída</span>
                    <span className="sm:hidden">Concluída</span>
                  </>
                ) : (
                  <>
                    <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin text-blue-600" />
                    <span className="hidden sm:inline">Importando Parcerias...</span>
                    <span className="sm:hidden">Importando...</span>
                  </>
                )}
              </CardTitle>
              {isComplete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </CardHeader>

            <CardContent className="p-3 sm:p-4 space-y-3 sm:space-y-4 overflow-y-auto flex-1">
              {/* Estatísticas - COMPACTAS */}
              <div className="grid grid-cols-3 md:grid-cols-5 gap-1.5 sm:gap-2">
                <div className="text-center p-1.5 sm:p-2 bg-gray-50 rounded">
                  <div className="text-base sm:text-xl font-bold text-gray-900">{progress.total}</div>
                  <div className="text-[10px] sm:text-xs text-gray-600">Total</div>
                </div>
                <div className="text-center p-1.5 sm:p-2 bg-blue-50 rounded">
                  <div className="text-base sm:text-xl font-bold text-blue-900">{progress.processed}</div>
                  <div className="text-[10px] sm:text-xs text-blue-600">Proc.</div>
                </div>
                <div className="text-center p-1.5 sm:p-2 bg-green-50 rounded">
                  <div className="text-base sm:text-xl font-bold text-green-900">{progress.created}</div>
                  <div className="text-[10px] sm:text-xs text-green-600">Novas</div>
                </div>
                <div className="text-center p-1.5 sm:p-2 bg-purple-50 rounded">
                  <div className="text-base sm:text-xl font-bold text-purple-900">{progress.updated}</div>
                  <div className="text-[10px] sm:text-xs text-purple-600">Atual.</div>
                </div>
                <div className="text-center p-1.5 sm:p-2 bg-red-50 rounded">
                  <div className="text-base sm:text-xl font-bold text-red-900">{progress.errors}</div>
                  <div className="text-[10px] sm:text-xs text-red-600">Erros</div>
                </div>
              </div>

              {/* Barra de progresso */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {isComplete ? 'Concluído' : `Processando linha ${progress.currentRow} de ${progress.total}`}
                  </span>
                  <span className="font-semibold text-blue-600">{progressPercentage}%</span>
                </div>
                <Progress value={progressPercentage} className="h-3" />
              </div>

              {/* ERROS NO TOPO - Destaque vermelho com orientações */}
              {errorItems.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5" />
                    Erros Encontrados ({errorItems.length}) - Veja como corrigir
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto bg-red-50 border-2 border-red-300 rounded-lg p-3">
                    {errorItems.map((item, index) => (
                      <div key={index} className="space-y-3">
                        {/* Cabeçalho do erro */}
                        <div className="bg-red-100 border border-red-300 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0" />
                            <span className="font-bold text-red-900 text-base">
                              📍 Linha {item.row} do Excel
                            </span>
                          </div>
                          <div className="text-sm text-red-800 bg-white rounded p-2 border border-red-200">
                            {item.message}
                          </div>
                        </div>

                        {/* Orientações detalhadas para cada erro */}
                        {item.errors && item.errors.length > 0 && (
                          <div className="space-y-3 pl-2">
                            {item.errors.map((err, errIdx) => (
                              <ErrorGuidanceCard 
                                key={errIdx}
                                field={err.field}
                                errorMessage={err.message}
                              />
                            ))}
                          </div>
                        )}

                        {/* Separador entre erros */}
                        {index < errorItems.length - 1 && (
                          <div className="border-t-2 border-red-200 my-4"></div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Dica geral */}
                  <Alert className="border-blue-200 bg-blue-50">
                    <Info className="w-4 h-4 text-blue-600" />
                    <AlertDescription className="text-blue-800 text-xs ml-2">
                      <strong>💡 Dica:</strong> Corrija os erros no arquivo Excel e faça o upload novamente. 
                      Consulte a aba "Instruções" do template para mais detalhes sobre cada campo.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* LOG DE EXECUÇÃO EM TEMPO REAL */}
              {items.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Log de Execução ({items.length} itens processados)
                  </h3>
                  <div className="space-y-1 max-h-40 sm:max-h-48 overflow-y-auto bg-slate-50 border border-slate-200 rounded-lg p-2 sm:p-3">
                    {items.map((item, index) => (
                      <div 
                        key={index} 
                        className={`flex items-start gap-2 p-2 rounded text-xs ${
                          item.status === 'error' 
                            ? 'bg-red-50 border border-red-200' 
                            : 'bg-white border border-slate-200'
                        }`}
                      >
                        {getStatusIcon(item.status)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-slate-700">Linha {item.row}</span>
                            {getActionBadge(item.action)}
                          </div>
                          <div className={`text-xs ${
                            item.status === 'error' ? 'text-red-700' : 'text-slate-600'
                          }`}>
                            {item.message}
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Elemento invisível para auto-scroll */}
                    <div ref={logEndRef} />
                  </div>
                </div>
              )}

              {/* Recomendações */}
              {recommendations.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Recomendações
                  </h3>
                  <div className="space-y-2 max-h-32 sm:max-h-40 overflow-y-auto">
                    {recommendations.map((rec, index) => (
                      <Alert key={index} className="border-blue-200 bg-blue-50">
                        {getRecommendationIcon(rec.type)}
                        <AlertDescription className="text-blue-800 text-xs sm:text-sm ml-2">
                          {rec.message}
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                </div>
              )}

              {/* Resumo Final - Apenas quando completo - COMPACTO */}
              {isComplete && (
                <div className="space-y-2">
                  {/* Mensagem de Sucesso */}
                  {progress.created > 0 || progress.updated > 0 ? (
                    <Alert className="border-green-200 bg-green-50 py-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <AlertDescription className="text-green-800 ml-2 text-xs">
                        <strong>Sucesso!</strong> {progress.created} criada(s), {progress.updated} atualizada(s)
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  {/* Avisos de Erro */}
                  {progress.errors > 0 && (
                    <Alert className="border-red-200 bg-red-50 py-2">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <AlertDescription className="text-red-800 ml-2 text-xs">
                        <strong>Atenção:</strong> {progress.errors} erro(s). Veja detalhes acima.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {/* Processando - Apenas durante importação */}
              {!isComplete && (
                <div className="text-center py-6 sm:py-8">
                  <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 animate-spin text-blue-600 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600">Processando linha {progress.currentRow} de {progress.total}...</p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-2">Aguarde, não feche esta janela</p>
                </div>
              )}
            </CardContent>

            {/* Botão de fechar fixo no rodapé (apenas quando completo) */}
            {isComplete && (
              <div className="border-t bg-white p-2 sm:p-3 shrink-0">
                <div className="flex justify-end gap-2">
                  <Button 
                    onClick={onClose} 
                    className="w-full sm:w-auto px-4 sm:px-6 bg-blue-600 hover:bg-blue-700"
                    size="default"
                  >
                    Entendi
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ImportProgressModal;
